/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Dec 12 2024
 **/

import { useEffect, useRef } from "react";

import useWhiteboardStore from "./stores/useWhiteboardStore";
import useNetworkPlayer from "./hooks/useNetworkPlayer";
import useExpandEventHandler from "./hooks/useExpandEventHandler";
import { WhiteboardHook } from "./hooks/useWhiteboard";
import {
	INetworkDrawingEnd,
	INetworkDrawingTo,
	INetworkDrawingStart,
	INetworkInit,
	IExpandWhiteboardHandlers
} from "./types/types";

export function useWhiteboardActions() {
	const whiteboard = useWhiteboardStore((state) => state.whiteboard);
	const setWhiteboardMode = useWhiteboardStore((state) => state.setWhiteboardMode);
	const userId = useWhiteboardStore((state) => state.userId);
	const setIsOpen = useWhiteboardStore((state) => state.setIsOpen);
	const setUserName = useWhiteboardStore((state) => state.setUserName);
	const setWindowState = useWhiteboardStore((state) => state.setWindowState);

	const whiteboardRef = useRef<WhiteboardHook>();
	const networkPlayer = useNetworkPlayer();
	const expandEventHandler = useExpandEventHandler();

	useEffect(() => {
		whiteboardRef.current = whiteboard;
	}, [whiteboard]);

	return {
		// general
		open: (name: string | undefined, whiteboardMode: WhiteboardMode, eventHandlers?: IExpandWhiteboardHandlers) => {
			setIsOpen(true);
			setUserName(name || "");
			setWindowState("normal");
			setWhiteboardMode(whiteboardMode);
			if (eventHandlers) expandEventHandler.add(eventHandlers);
		},
		close: () => setIsOpen(false),
		reset: () => whiteboardRef.current?.resetWhiteboard(),
		// presentation
		setDocumentPageCountUpdated: (pageCount: number) => {
			whiteboardRef.current?.initPageCount(pageCount);
		},
		setDocumentPageNumberUpdated: (pageNumber: number) => {
			whiteboardRef.current?.updatePageNumber(pageNumber);
		},
		// presentation, screen
		contentLoaded: (resolution: Resolution, targetDivId: string) => {
			whiteboardRef.current?.updateResolution(resolution);
			whiteboardRef.current?.setParent(targetDivId);
		},
		toggleToolbar: (enable: boolean) => {
			setWindowState(enable ? "minimize" : "normal");
		},
		// image src
		setSelectedFilePath: (selectedFilePath: string) => {
			if (selectedFilePath == "") return;
			const isDev = window.location.href.includes("localhost");
			const filePath = isDev ? "use-sample" : selectedFilePath;
			whiteboardRef.current?.updateImageButtonClicked(filePath);
			whiteboardRef.current?.updateImageSrc(userId, filePath);
		},
		// network
		network: {
			initPlayer: (props: INetworkInit) => networkPlayer.init(props),
			addNetworkPlayer: (id: string) => whiteboardRef.current?.addUser(id),
			drawingStart: (props: INetworkDrawingStart) => networkPlayer.updateDrawingStart(props),
			drawingTo: (props: INetworkDrawingTo) => networkPlayer.updateDrawingTo(props),
			drawingEnd: (props: INetworkDrawingEnd) => networkPlayer.updateDrawingEnd(props),
			history: (state: HistoryState) => networkPlayer.updateHistory(state),
			reset: () => networkPlayer.reset()
		}
	};
}
